// Step sequencer / beat-maker (MPC/boom-bap style). A grid of steps × rows plays
// notes through any instrument in the library (drum kit, sampler, GM, soundfont,
// synth), loops at the project tempo for live preview, and can commit the pattern
// to the DAW timeline as a MIDI clip on a new instrument track. Shown on demand.
import { Store } from "../state/store";
import { AudioEngine } from "../audio/engine";
import { el, uid } from "../util";
import { openModal, toast } from "./modal";
import { createInstrument } from "../audio/gm";
import { Instrument } from "../audio/instrument";
import { SYNTH_PRESETS, SYNTH_PRESET_LABELS } from "../audio/instrument";
import { GM_NAMES, GM_FAMILIES, SOUNDFONTS, DEFAULT_SOUNDFONT, gmDisplay } from "../audio/gm";
import { DRUM_KITS } from "../audio/drums";
import { TrackInstrument, SavedSequence, newTrack, newMidiClip } from "../state/model";

const noteName = (m: number) => ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"][((m % 12) + 12) % 12] + (Math.floor(m / 12) - 1);
const DRUM_PADS: [number, string][] = [
  [36, "Kick"], [38, "Snare"], [42, "Cl Hat"], [46, "Op Hat"], [39, "Clap"], [37, "Rim"],
  [45, "Low Tom"], [50, "Hi Tom"], [51, "Ride"], [49, "Crash"], [56, "Cowbell"], [54, "Tamb"],
];

export function openSequencer(store: Store, engine: AudioEngine) {
  let def: TrackInstrument = { type: "drums", kit: "boombap" };
  let steps = 16;
  let octave = 0;
  const active = new Set<string>(); // `${note}:${step}`
  let inst: Instrument | null = null;

  // playback
  let playing = false, schedTimer: any = null, raf = 0, startTime = 0, curStep = -1;
  let along = false, startedEngine = false; // "play along with timeline" state
  let splitTracks = false; // drums: commit each pad to its own timeline track
  // save-to-project / unsaved-changes tracking
  let dirty = false;              // pattern edited since last save/commit
  let currentSeqId: string | null = null; // id of the loaded/saved sequence
  let seqName = "";               // its name
  const markDirty = () => { dirty = true; if (saveBtn) saveBtn.textContent = "💾 Save*"; };

  const body = el("div", { class: "seq" });
  const barEl = el("div", { class: "seq-bar" });
  const gridEl = el("div", { class: "seq-grid" });
  body.append(barEl, gridEl);

  const secPerBeat = () => 60 / store.project.tempo;
  const stepDur = () => secPerBeat() / 4; // 16th notes

  function rows(): { note: number; label: string }[] {
    if (def.type === "sampler") return def.samples.map((s) => ({ note: s.note ?? 36, label: s.name.slice(0, 12) }));
    if (def.type === "drums") return DRUM_PADS.map(([note, label]) => ({ note, label }));
    const base = 48 + octave * 12; // melodic: one octave, high on top
    const arr: { note: number; label: string }[] = [];
    for (let m = base + 12; m >= base; m--) arr.push({ note: m, label: noteName(m) });
    return arr;
  }
  const melodic = () => def.type === "gm" || def.type === "soundfont" || def.type === "synth";

  function rebuildInstrument() {
    stop();
    inst?.dispose();
    inst = createInstrument(engine.ctx, def, store.buffers);
    inst.output.connect(engine.previewDestination());
  }

  const audition = (note: number, vel = 0.9) => { engine.resume(); inst?.noteOn(note, vel); setTimeout(() => inst?.noteOff(note), 220); };

  // ---- playback (lookahead scheduler, loops the pattern) ----
  function startLoop() {
    engine.resume();
    playing = true; refreshTransportBtns();
    startTime = engine.ctx.currentTime + 0.12;
    let nextStep = 0;
    const rs = rows();
    schedTimer = setInterval(() => {
      const dur = stepDur();
      const ahead = engine.ctx.currentTime + 0.15;
      while (startTime + nextStep * dur < ahead) {
        const t = startTime + nextStep * dur, s = nextStep % steps;
        for (const r of rs) if (active.has(`${r.note}:${s}`)) inst?.scheduleNote(r.note, 0.9, t, dur * 0.9);
        nextStep++;
      }
    }, 25);
    const uiTick = () => {
      if (!playing) return;
      const s = Math.floor((engine.ctx.currentTime - startTime) / stepDur()) % steps;
      if (s >= 0 && s !== curStep) { highlight(s); curStep = s; }
      raf = requestAnimationFrame(uiTick);
    };
    raf = requestAnimationFrame(uiTick);
  }
  // Preview the pattern on its own.
  function play() {
    if (playing) { stop(); return; }
    along = false; startedEngine = false;
    startLoop();
  }
  // Play the pattern together with the DAW timeline — start the transport (from
  // the current position) if it isn't already rolling, and loop in step with it.
  function playAlong() {
    if (playing) { stop(); return; }
    along = true;
    startedEngine = !engine.playing;
    if (startedEngine) void engine.play(engine.position());
    startLoop();
  }
  function stop() {
    if (!playing && !schedTimer) return;
    playing = false; clearInterval(schedTimer); schedTimer = null; cancelAnimationFrame(raf);
    inst?.allNotesOff(); curStep = -1; highlight(-1);
    // if we started the transport for "play along", stop it again
    if (along && startedEngine && engine.playing) engine.pause();
    along = false; startedEngine = false;
    refreshTransportBtns();
  }
  function refreshTransportBtns() {
    if (playBtn) { playBtn.textContent = playing && !along ? "⏹ Stop" : "▶ Play"; playBtn.classList.toggle("on", playing && !along); }
    if (alongBtn) { alongBtn.textContent = playing && along ? "⏹ Stop" : "▶ + Song"; alongBtn.classList.toggle("on", playing && along); }
  }
  function highlight(step: number) {
    gridEl.querySelectorAll(".seq-cell.playing").forEach((c) => c.classList.remove("playing"));
    if (step >= 0) gridEl.querySelectorAll(`.seq-cell[data-s="${step}"]`).forEach((c) => c.classList.add("playing"));
  }

  // ---- save the pattern into the project (recall & edit later) ----
  function snapshot(name: string): SavedSequence {
    return { id: currentSeqId ?? uid("seq"), name, def: JSON.parse(JSON.stringify(def)), steps, octave, tempo: store.project.tempo, active: [...active] };
  }
  function saveSequence(after?: () => void) {
    const commit = (name: string) => {
      const seq = snapshot(name);
      store.upsertSequence(seq);
      currentSeqId = seq.id; seqName = name; dirty = false;
      toast(`Saved sequence "${name}" to the project.`);
      render(); after?.();
    };
    if (currentSeqId && seqName) { commit(seqName); return; } // overwrite existing in place
    const input = el("input", { attrs: { type: "text", value: seqName || (def.type === "drums" ? "Beat" : "Sequence") } }) as HTMLInputElement;
    const body = el("div", { class: "seq-save" }, [el("p", { class: "hint", text: "Name this sequence to save it in the project." }), input]);
    openModal("Save sequence", body, [
      { label: "Cancel", onClick: () => {} },
      { label: "Save", primary: true, onClick: () => { const n = input.value.trim(); if (!n) { toast("Enter a name."); return false; } commit(n); } },
    ]);
    setTimeout(() => { input.focus(); input.select(); }, 0);
  }
  function loadSequence(id: string) {
    const seq = store.project.sequences?.find((s) => s.id === id);
    if (!seq) return;
    def = JSON.parse(JSON.stringify(seq.def)); steps = seq.steps; octave = seq.octave;
    active.clear(); for (const k of seq.active) active.add(k);
    currentSeqId = seq.id; seqName = seq.name; dirty = false;
    if (seq.tempo && seq.tempo !== store.project.tempo) store.setTempo(seq.tempo);
    rebuildInstrument(); render();
  }

  function addToTimeline() {
    const dur = stepDur();
    const at = Math.max(0, engine.position());
    const len = steps * dur;
    // Drums with "separate tracks": one instrument track per pad that has hits.
    if (def.type === "drums" && splitTracks) {
      let made = 0;
      for (const r of rows()) {
        const notes: { midi: number; start: number; dur: number; vel: number }[] = [];
        for (let s = 0; s < steps; s++) if (active.has(`${r.note}:${s}`)) notes.push({ midi: r.note, start: s * dur, dur: dur * 0.9, vel: 0.9 });
        if (!notes.length) continue;
        const t = newTrack(store.project.tracks.length, "instrument");
        t.name = r.label;
        t.instrument = JSON.parse(JSON.stringify(def));
        store.addTrack(t);
        store.addClip(t.id, newMidiClip(at, len, notes, r.label));
        made++;
      }
      if (!made) { toast("Pattern is empty — add some steps first."); return; }
      dirty = false; if (saveBtn) saveBtn.textContent = "💾 Save";
      toast(`Added ${made} drum track${made > 1 ? "s" : ""} (one per pad).`, 4000);
      return;
    }
    const notes: { midi: number; start: number; dur: number; vel: number }[] = [];
    for (const r of rows()) for (let s = 0; s < steps; s++) if (active.has(`${r.note}:${s}`)) notes.push({ midi: r.note, start: s * dur, dur: dur * 0.9, vel: 0.9 });
    if (!notes.length) { toast("Pattern is empty — add some steps first."); return; }
    const t = newTrack(store.project.tracks.length, "instrument");
    t.name = def.type === "drums" ? "Beat" : def.type === "sampler" ? "Sampler" : "Seq";
    t.instrument = JSON.parse(JSON.stringify(def));
    store.addTrack(t);
    store.addClip(t.id, newMidiClip(at, len, notes, t.name));
    dirty = false; if (saveBtn) saveBtn.textContent = "💾 Save";
    toast(`Added ${notes.length}-note pattern → new "${t.name}" track.`, 4000);
  }

  // ---- UI ----
  let playBtn: HTMLButtonElement, alongBtn: HTMLButtonElement, saveBtn: HTMLButtonElement;
  function render() {
    barEl.innerHTML = ""; gridEl.innerHTML = "";
    // source
    const srcSel = el("select") as HTMLSelectElement;
    for (const [v, label] of [["drums", "Drum Kit"], ["sampler", "Sampler"], ["gm", "General MIDI"], ["soundfont", "Soundfont"], ["synth", "Synth"]] as [string, string][]) {
      const o = el("option", { text: label, attrs: { value: v } }) as HTMLOptionElement; if (def.type === v) o.selected = true; srcSel.append(o);
    }
    srcSel.addEventListener("change", () => {
      const v = srcSel.value;
      def = v === "drums" ? { type: "drums", kit: "boombap" } : v === "sampler" ? { type: "sampler", mode: "kit", samples: [] }
        : v === "gm" ? { type: "gm", program: 0, soundfont: DEFAULT_SOUNDFONT } : v === "soundfont" ? { type: "soundfont", name: "acoustic_grand_piano", soundfont: DEFAULT_SOUNDFONT }
        : { type: "synth", preset: "lead" };
      markDirty(); rebuildInstrument(); render();
    });
    barEl.append(labeled("Sound", srcSel), subControl());

    // octave (melodic)
    if (melodic()) {
      const dn = mkBtn("Oct −", () => { octave = Math.max(-3, octave - 1); markDirty(); rebuildInstrument(); render(); });
      const up = mkBtn("Oct +", () => { octave = Math.min(3, octave + 1); markDirty(); rebuildInstrument(); render(); });
      barEl.append(el("div", { class: "seq-grp" }, [dn, up]));
    }
    // steps (16 = 1 bar of 16ths, up to 128 = 8 bars)
    const stepSel = el("select") as HTMLSelectElement;
    for (const n of [16, 32, 64, 128]) { const o = el("option", { text: `${n} steps`, attrs: { value: String(n) } }) as HTMLOptionElement; if (n === steps) o.selected = true; stepSel.append(o); }
    stepSel.addEventListener("change", () => { steps = Number(stepSel.value); markDirty(); render(); });
    barEl.append(labeled("Steps", stepSel));

    // tempo — edits the project BPM (kept in sync with the DAW)
    const bpmIn = el("input", { attrs: { type: "number", min: "20", max: "300", step: "1", value: String(store.project.tempo) } }) as HTMLInputElement;
    bpmIn.style.width = "56px";
    bpmIn.addEventListener("change", () => {
      store.setTempo(Number(bpmIn.value) || store.project.tempo); // also time-scales existing MIDI clips
      bpmIn.value = String(store.project.tempo);
      markDirty();
      if (playing) { const wasAlong = along; stop(); wasAlong ? playAlong() : play(); } // re-anchor the loop to the new tempo
    });
    barEl.append(labeled("BPM", bpmIn));

    // saved-sequence loader
    const seqs = store.project.sequences ?? [];
    if (seqs.length) {
      const load = el("select") as HTMLSelectElement;
      load.append(el("option", { text: "Load saved…", attrs: { value: "" } }));
      for (const s of seqs) { const o = el("option", { text: s.name, attrs: { value: s.id } }) as HTMLOptionElement; if (s.id === currentSeqId) o.selected = true; load.append(o); }
      load.addEventListener("change", () => { if (load.value) loadSequence(load.value); });
      barEl.append(labeled("Saved", load));
    }

    playBtn = mkBtn("▶ Play", play); playBtn.classList.add("active");
    alongBtn = mkBtn("▶ + Song", playAlong); alongBtn.title = "Play/audition along with the DAW timeline";
    saveBtn = mkBtn(dirty ? "💾 Save*" : "💾 Save", () => saveSequence()); saveBtn.title = "Save this sequence into the project (recall & edit later)";
    refreshTransportBtns();
    barEl.append(el("div", { class: "seq-grp" }, [playBtn, alongBtn, saveBtn, mkBtn("Clear", () => { active.clear(); markDirty(); render(); }), mkBtn("＋ Add to timeline", addToTimeline)]));
    // drums: option to commit each pad to its own track
    if (def.type === "drums") {
      const cb = el("input", { attrs: { type: "checkbox" } }) as HTMLInputElement;
      cb.checked = splitTracks;
      cb.addEventListener("change", () => { splitTracks = cb.checked; });
      const lab = el("label", { class: "seq-check", attrs: { title: "Add to timeline as one track per drum pad instead of a single beat track" } }, [cb, el("span", { text: "Separate tracks" })]);
      barEl.append(lab);
    }

    // grid
    const rs = rows();
    if (!rs.length) { gridEl.append(el("p", { class: "hint", text: "Load samples into the Sampler to get pads." })); return; }
    for (const r of rs) {
      const rowEl = el("div", { class: "seq-row" });
      const lbl = el("button", { class: "seq-label", text: r.label, attrs: { title: "Preview" } });
      lbl.addEventListener("click", () => audition(r.note));
      rowEl.append(lbl);
      for (let s = 0; s < steps; s++) {
        const cell = el("div", { class: "seq-cell" + (active.has(`${r.note}:${s}`) ? " on" : "") + (s % 4 === 0 ? " beat" : ""), attrs: { "data-s": String(s) } });
        // Toggle the step. Audition on add only when stopped — while the loop is
        // playing you'll hear it in the sequence (use the sound-name button to
        // preview manually).
        cell.addEventListener("mousedown", () => { const k = `${r.note}:${s}`; if (active.has(k)) active.delete(k); else { active.add(k); if (!playing) audition(r.note, 0.9); } cell.classList.toggle("on"); markDirty(); });
        rowEl.append(cell);
      }
      gridEl.append(rowEl);
    }
  }

  function subControl(): HTMLElement {
    if (def.type === "drums") {
      const s = el("select") as HTMLSelectElement;
      for (const k of Object.keys(DRUM_KITS)) { const o = el("option", { text: DRUM_KITS[k].label, attrs: { value: k } }) as HTMLOptionElement; if (k === def.kit) o.selected = true; s.append(o); }
      s.addEventListener("change", () => { def = { type: "drums", kit: s.value }; markDirty(); rebuildInstrument(); });
      return labeled("Kit", s);
    }
    if (def.type === "synth") {
      const s = el("select") as HTMLSelectElement;
      for (const k of Object.keys(SYNTH_PRESETS)) { const o = el("option", { text: SYNTH_PRESET_LABELS[k] ?? k, attrs: { value: k } }) as HTMLOptionElement; if (k === def.preset) o.selected = true; s.append(o); }
      s.addEventListener("change", () => { def = { type: "synth", preset: s.value }; markDirty(); rebuildInstrument(); });
      return labeled("Preset", s);
    }
    if (def.type === "gm" || def.type === "soundfont") {
      const cur = def.type === "gm" ? GM_NAMES[def.program] : def.name;
      const s = el("select") as HTMLSelectElement;
      for (let fam = 0; fam < 16; fam++) {
        const grp = document.createElement("optgroup"); grp.label = GM_FAMILIES[fam];
        for (let p = fam * 8; p < fam * 8 + 8; p++) { const o = el("option", { text: gmDisplay(GM_NAMES[p]), attrs: { value: String(p) } }) as HTMLOptionElement; if (GM_NAMES[p] === cur) o.selected = true; grp.append(o); }
        s.append(grp);
      }
      const bank = def.type === "soundfont" ? (def.soundfont || DEFAULT_SOUNDFONT) : (def.soundfont || DEFAULT_SOUNDFONT);
      s.addEventListener("change", () => { const p = Number(s.value); def = { type: "soundfont", name: GM_NAMES[p], soundfont: bank }; markDirty(); rebuildInstrument(); });
      return labeled("Instrument", s);
    }
    // sampler → load button
    const load = mkBtn("＋ Load samples", () => fileIn.click());
    const fileIn = el("input", { attrs: { type: "file", accept: "audio/*", multiple: "true" } }) as HTMLInputElement;
    fileIn.style.display = "none";
    fileIn.addEventListener("change", async () => {
      const files = Array.from(fileIn.files || []); fileIn.value = "";
      if (def.type !== "sampler") return;
      const samples = [...def.samples];
      const pad = [36, 38, 42, 46, 39, 37, 45, 50, 51, 49, 56, 54];
      for (const f of files) {
        try { const buf = await engine.ctx.decodeAudioData(await f.arrayBuffer()); const a = store.addAsset(buf, f.name); samples.push({ assetId: a.id, name: f.name, note: pad[samples.length] ?? 36 + samples.length }); }
        catch { toast(`Couldn't decode ${f.name}.`, 5000); }
      }
      def = { type: "sampler", mode: "kit", samples };
      markDirty(); rebuildInstrument(); render();
    });
    return el("div", { class: "seq-grp" }, [load, fileIn]);
  }

  // Warn before closing if the pattern has unsaved edits.
  function confirmDone() {
    const body2 = el("div", {}, [el("p", { text: "This sequence has unsaved changes. Save it to the project before closing?" })]);
    openModal("Unsaved sequence", body2, [
      { label: "Cancel", onClick: () => {} },
      { label: "Discard & close", onClick: () => { dirty = false; close(); } },
      { label: "Save & close", primary: true, onClick: () => { saveSequence(() => close()); } },
    ]);
  }

  rebuildInstrument();
  render();
  const close = openModal("Step Sequencer", body, [{ label: "Done", primary: true, onClick: () => { if (!dirty) return; confirmDone(); return false; } }]);
  // stop + free the preview instrument when the panel closes
  const mo = new MutationObserver(() => { if (!document.body.contains(body)) { stop(); inst?.dispose(); inst = null; mo.disconnect(); } });
  mo.observe(document.body, { childList: true, subtree: true });
  void close;
}

function labeled(label: string, ctrl: HTMLElement): HTMLElement { return el("div", { class: "seq-grp" }, [el("span", { class: "hint", text: label }), ctrl]); }
function mkBtn(label: string, on: () => void): HTMLButtonElement { const b = el("button", { text: label }); b.addEventListener("click", on); return b; }
